import React, { useMemo, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Copy, Download, MessageCircle, Mail, Share2, Link as LinkIcon } from "lucide-react";
import { toast } from "sonner";

const FEEDBACK_PATH = "/feedback";

export default function AdminShareTools() {
  // Prefer the configured production site URL (set in frontend/.env). Falls back to the
  // current preview origin so QR codes still work in dev.
  const siteOrigin = (process.env.REACT_APP_SITE_URL || window.location.origin).replace(/\/$/, "");
  const feedbackUrl = `${siteOrigin}${FEEDBACK_PATH}`;

  const [clientName, setClientName] = useState("");
  const [service, setService] = useState("Astrology Consultation");

  const wa = useMemo(() => (
`Namaste ${clientName || "[Name]"} 🙏

Thank you for choosing YourAstroHub for your ${service}.
It would mean a lot if you could share a quick review of your experience — it helps other seekers find guidance.

🌟 ${feedbackUrl}

With warmth,
Bhupaal Bisht`
  ), [clientName, service, feedbackUrl]);

  const sms = useMemo(() => (
`YourAstroHub: Thank you ${clientName || "[Name]"} for your ${service}. Please share your feedback here: ${feedbackUrl}`
  ), [clientName, service, feedbackUrl]);

  const email = useMemo(() => ({
    subject: `Your feedback matters — YourAstroHub`,
    body:
`Dear ${clientName || "[Name]"},

Thank you for choosing YourAstroHub for your ${service}. Your journey means a great deal to us.

Would you take a moment to share your experience? Your words help other seekers take the first step towards clarity.

Share your review here: ${feedbackUrl}

With gratitude,
Bhupaal Bisht
YourAstroHub`,
  }), [clientName, service, feedbackUrl]);

  const copy = (text, label = "Copied") => {
    navigator.clipboard.writeText(text).then(() => toast.success(label));
  };

  const downloadQR = () => {
    const canvas = document.getElementById("feedback-qr");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = "yourastrohub-feedback-qr.png";
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const openWa = () => window.open(`https://wa.me/?text=${encodeURIComponent(wa)}`, "_blank");
  const openSms = () => window.open(`sms:?body=${encodeURIComponent(sms)}`);
  const openMail = () => window.open(`mailto:?subject=${encodeURIComponent(email.subject)}&body=${encodeURIComponent(email.body)}`);

  return (
    <div data-testid="admin-share-tools" className="space-y-8">
      <div>
        <h1 className="font-serif text-4xl">Share Feedback Tools</h1>
        <p className="text-foreground/60 mt-1">One-click ways to request reviews from clients — WhatsApp, SMS, email or a QR code.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Personalisation */}
        <div className="lg:col-span-1 rounded-3xl border border-foreground/10 p-6 bg-background space-y-4 h-fit">
          <div className="eyebrow">Personalise</div>
          <div>
            <Label>Client name</Label>
            <Input value={clientName} onChange={(e) => setClientName(e.target.value)} placeholder="Rahul Sharma" className="rounded-full mt-2" data-testid="share-name" />
          </div>
          <div>
            <Label>Service availed</Label>
            <Input value={service} onChange={(e) => setService(e.target.value)} className="rounded-full mt-2" data-testid="share-service" />
          </div>
          <div className="rounded-2xl border border-foreground/10 p-4 flex items-center gap-3 text-sm">
            <LinkIcon className="w-4 h-4 text-[#D4AF37]" />
            <span className="truncate flex-1">{feedbackUrl}</span>
            <Button size="sm" variant="outline" onClick={() => copy(feedbackUrl, "Link copied")} data-testid="copy-link">
              <Copy className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* QR */}
        <div className="rounded-3xl border border-foreground/10 p-6 bg-background text-center">
          <div className="eyebrow mb-4">QR code</div>
          <div className="mx-auto w-52 h-52 grid place-items-center bg-white rounded-2xl border border-foreground/10">
            <QRCodeCanvas id="feedback-qr" value={feedbackUrl} size={180} level="H" includeMargin bgColor="#ffffff" fgColor="#0B1F3A" />
          </div>
          <p className="text-xs text-foreground/60 mt-3">Print on your consultation slip, business card or clinic wall.</p>
          <Button onClick={downloadQR} data-testid="download-qr" className="mt-4 rounded-full bg-[#D4AF37] hover:bg-[#AD974F] text-[#0B1F3A]">
            <Download className="w-4 h-4 mr-2" />Download PNG
          </Button>
        </div>

        {/* WhatsApp */}
        <div className="rounded-3xl border border-foreground/10 p-6 bg-background flex flex-col">
          <div className="eyebrow mb-4 flex items-center gap-2"><MessageCircle className="w-4 h-4 text-[#25D366]" /> WhatsApp template</div>
          <Textarea readOnly value={wa} className="rounded-2xl flex-1 min-h-[220px]" data-testid="wa-template" />
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => copy(wa, "Message copied")} className="rounded-full flex-1" data-testid="copy-wa">
              <Copy className="w-3.5 h-3.5 mr-2" />Copy
            </Button>
            <Button onClick={openWa} className="rounded-full flex-1 bg-[#25D366] hover:bg-[#20b957] text-white" data-testid="open-wa">
              <MessageCircle className="w-3.5 h-3.5 mr-2" />Open in WhatsApp
            </Button>
          </div>
        </div>

        {/* SMS */}
        <div className="rounded-3xl border border-foreground/10 p-6 bg-background flex flex-col">
          <div className="eyebrow mb-4 flex items-center gap-2"><Share2 className="w-4 h-4 text-[#D4AF37]" /> SMS template</div>
          <Textarea readOnly value={sms} className="rounded-2xl flex-1 min-h-[220px]" data-testid="sms-template" />
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => copy(sms, "SMS copied")} className="rounded-full flex-1" data-testid="copy-sms">
              <Copy className="w-3.5 h-3.5 mr-2" />Copy
            </Button>
            <Button onClick={openSms} className="rounded-full flex-1 bg-[#0B1F3A] text-[#F5E8D8] hover:bg-[#0F284B]" data-testid="open-sms">Send SMS</Button>
          </div>
        </div>

        {/* Email */}
        <div className="rounded-3xl border border-foreground/10 p-6 bg-background flex flex-col lg:col-span-2">
          <div className="eyebrow mb-4 flex items-center gap-2"><Mail className="w-4 h-4 text-[#AD974F]" /> Email template</div>
          <Input readOnly value={email.subject} className="rounded-full mb-2" />
          <Textarea readOnly value={email.body} className="rounded-2xl flex-1 min-h-[220px]" data-testid="email-template" />
          <div className="mt-4 flex gap-2">
            <Button variant="outline" onClick={() => copy(`${email.subject}\n\n${email.body}`, "Email copied")} className="rounded-full flex-1" data-testid="copy-email">
              <Copy className="w-3.5 h-3.5 mr-2" />Copy
            </Button>
            <Button onClick={openMail} className="rounded-full flex-1 bg-[#AD974F] hover:bg-[#8b7838] text-white" data-testid="open-email">
              <Mail className="w-3.5 h-3.5 mr-2" />Compose email
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
